import React, { createContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../API/baseURL';
import API_ENDPOINTS, { getUserInfoFromAuth } from '../apiLayer/vsCodeAuthAPIs/authAPIs';
import { local } from '../utils/constants';
import { getPriceId, SUPPORTED_PLANS } from '../utils/stripePriceIds';
import { fetchPaymentProviders, getActiveProvider, resolvePlanId, WHOP } from '../utils/paymentProvider';
import { createWhopCheckout, createWhopLifetimeCheckout } from '../apiLayer/dashBoardAPIs/whopAPIs';

const serverBaseUrl = local ? "http://127.0.0.1:8000" : "https://backend.withsix.co";

export const VsCodeAuthContext = createContext();

// Security: Allowed domains for redirect URLs to prevent open redirect attacks
const ALLOWED_REDIRECT_DOMAINS = [
  'app.trysixth.com',
  'trysixth.com',
  'backend.withsix.co',
  'standard.trysixth.com',
  'localhost',
  '127.0.0.1'
];

// Security: Allowed protocols for callback URLs
const ALLOWED_PROTOCOLS = ['https:', 'http:', 'vscode:'];

/**
 * Determines the correct dashboard route based on user's sixth_plan
 * @param {string} sixthPlan - The user's plan ('sixth_credit' or 'byok')
 * @returns {string} - The dashboard route
 */
const getDashboardRoute = () => {
  // All users go to the unified dashboard
  return '/vscode/dashboard';
};

/**
 * Validates a URL to prevent open redirect vulnerabilities
 * @param {string} url - The URL to validate
 * @param {boolean} allowVscode - Whether to allow vscode:// protocol
 * @returns {boolean} - Whether the URL is safe to redirect to
 */
const isValidRedirectUrl = (url, allowVscode = false) => {
  if (!url) return false;

  try {
    // Allow relative URLs (they're safe)
    if (url.startsWith('/') && !url.startsWith('//')) {
      return true;
    }

    // Allow vscode:// protocol for VS Code extension callbacks
    if (allowVscode && url.startsWith('vscode://')) {
      return true;
    }

    const urlObj = new URL(url, window.location.origin);

    // Check protocol
    if (!ALLOWED_PROTOCOLS.includes(urlObj.protocol)) {
      return false;
    }

    // For vscode: protocol, always allow
    if (urlObj.protocol === 'vscode:') {
      return allowVscode;
    }

    // Check if domain is in allowed list
    const hostname = urlObj.hostname.toLowerCase();
    return ALLOWED_REDIRECT_DOMAINS.some(domain =>
      hostname === domain || hostname.endsWith('.' + domain)
    );
  } catch (e) {
    // Invalid URL
    return false;
  }
};

export const VsCodeAuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setErrorState] = useState(null);
  const navigate = useNavigate();
  
  // Check for cached authentication on context initialization
  React.useEffect(() => {
    const checkCachedAuth = () => {
      const isAuthenticated = localStorage.getItem('is_authenticated');
      const authData = localStorage.getItem('auth_data');
      
      if (isAuthenticated === 'true' && authData) {
        try {
          const parsedAuthData = JSON.parse(authData);
          // Check if auth data is not too old (e.g., 7 days)
          const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
          
          if (parsedAuthData.timestamp && parsedAuthData.timestamp > sevenDaysAgo) {
            setUser({
              email: parsedAuthData.email,
              apikey: parsedAuthData.apikey
            });
          } else {
            // Auth data is too old, clear it
            localStorage.removeItem('is_authenticated');
            localStorage.removeItem('auth_data');
          }
        } catch (error) {
          localStorage.removeItem('is_authenticated');
          localStorage.removeItem('auth_data');
        }
      }
    };
    
    checkCachedAuth();
  }, []);

  const setError = (errorMessage) => {
    setErrorState(errorMessage);
    if (errorMessage) {
      alert(errorMessage);
    }
  };

  const processSuccessfulAuth = async (data, isSignUp) => {
    // Security: Removed sensitive logging of auth data
    
    const vsCodeApiKey = isSignUp ? data.uid : data.apikey;
    const vsCodeEmail = isSignUp ? data.email : data.email;
    const vsCodeJwt = data.access_token.access_token;
    const webJwt = data.temp_access_token?.access_token;

    // Cache the JWT tokens and full auth data for future use
    if (webJwt) {
      localStorage.setItem('jwt_token', webJwt);
      localStorage.setItem('web_access_token', webJwt);
    }
    if (vsCodeJwt) {
      localStorage.setItem('vscode_jwt_token', vsCodeJwt);
    }
    if (vsCodeApiKey) {
      localStorage.setItem('api_key', vsCodeApiKey);
    }
    if (vsCodeEmail) {
      localStorage.setItem('user_email', vsCodeEmail);
    }
    
    // Cache full auth response for persistent login
    localStorage.setItem('auth_data', JSON.stringify({
      apikey: vsCodeApiKey,
      email: vsCodeEmail,
      access_token: data.access_token,
      temp_access_token: data.temp_access_token,
      timestamp: Date.now()
    }));
    
    // Set user as authenticated
    localStorage.setItem('is_authenticated', 'true');

    // Get callback URL, referrer and redirect URL from params
    const urlParams = new URLSearchParams(window.location.search);
    const urlCallbackUrl = urlParams.get('callback_url') || urlParams.get('callback');
    const urlReferrer = urlParams.get('referrer');
    const redirectUrl = urlParams.get('redirect');
    const codeParam = urlParams.get('code'); // OAuth code indicates callback
    
    // Use cached values when URL params are not present or during OAuth callbacks
    const isOAuthCallback = !!codeParam;
    const callbackUrl = urlCallbackUrl || localStorage.getItem('vscode_callback_url');
    const referrer = urlReferrer || localStorage.getItem('vscode_referrer');
    
    // Security: Removed verbose redirect analysis logging

    // Clean up URL before redirecting (remove code param)
    const cleanupUrl = () => {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.delete('code'); // Remove OAuth code
      const newSearch = currentParams.toString();
      const cleanUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
      window.history.replaceState({}, document.title, cleanUrl);
    };
    
    // Priority 1: CLI callback URL (for CLI authentication)
    if (referrer === 'cli' && callbackUrl) {
      // Security: Validate callback URL before redirecting
      if (!isValidRedirectUrl(callbackUrl, false)) {
        console.error('Security: Invalid CLI callback URL rejected:', callbackUrl);
        navigate('/vscode/dashboard');
        return;
      }

      cleanupUrl();

      // Add JWT token as query parameter for CLI
      const urlObj = new URL(callbackUrl);
      urlObj.searchParams.set('token', vsCodeJwt);

      window.location.href = urlObj.toString();
      return;
    } else if (callbackUrl && callbackUrl.startsWith('vscode://')) {
      // Priority 2: VSCode callback URL (for VSCode authentication)
      cleanupUrl();
      navigate(`/vscode/onboarding?callback_url=${encodeURIComponent(callbackUrl)}`);
    } else if (referrer === 'vscode') {
      // Priority 3: VSCode referrer (fallback VSCode auth)
      cleanupUrl();
      navigate(`/vscode/onboarding?apikey=${vsCodeApiKey}&email=${encodeURIComponent(vsCodeEmail)}&jwt=${vsCodeJwt}`);
    } else if (referrer === 'teams') {
      // Priority 4: Teams invite referrer - join team and redirect to dashboard
      const pendingTeamJoinId = localStorage.getItem('pending_team_join_id');

      // Only call /teams/join for existing users (sign-in)
      // For new signups, the /authentication/register endpoint handles adding to team
      if (pendingTeamJoinId && vsCodeJwt && !isSignUp) {
        try {
          // Call backend to add existing user to the team
          const teamJoinResponse = await fetch(`${serverBaseUrl}/teams/join`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${vsCodeJwt}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              team_id: pendingTeamJoinId,
              user_email: vsCodeEmail
            })
          });

          if (teamJoinResponse.ok) {
            const joinData = await teamJoinResponse.json();
            console.log('Successfully joined team:', pendingTeamJoinId, joinData);
          } else {
            const errorData = await teamJoinResponse.json().catch(() => ({}));
            console.error('Failed to join team:', errorData.message || errorData.detail);
          }
        } catch (err) {
          console.error('Error joining team:', err);
        }
      }

      // Clean up team-related localStorage
      localStorage.removeItem('pending_team_join_id');
      localStorage.removeItem('vscode_referrer');

      cleanupUrl();
      // Redirect to onboarding then dashboard
      navigate('/vscode/onboarding');
    } else if (redirectUrl) {
      // Parse redirect URL to extract plan and billing info
      const decodedUrl = decodeURIComponent(redirectUrl);

      // Security: Validate redirect URL before using
      if (!isValidRedirectUrl(decodedUrl, false)) {
        console.error('Security: Invalid redirect URL rejected:', decodedUrl);
        navigate('/vscode/dashboard');
        return;
      }

      const urlObj = new URL(decodedUrl, window.location.origin);
      const planParam = urlObj.searchParams.get('plan');
      const billingParam = urlObj.searchParams.get('billing');

      if (planParam && SUPPORTED_PLANS.includes(planParam) && webJwt) {
        // User came from pricing page, redirect directly to checkout
        await createStripeCheckoutDirect(planParam, billingParam === 'yearly', webJwt);
      } else {
        // Regular redirect
        navigate(decodedUrl);
      }
    } else {
      // Go to dashboard - determine which one based on sixth_plan
      if (webJwt) {
        try {
          // Navigate to unified dashboard
          navigate(getDashboardRoute());
        } catch (err) {
          console.error('Error during routing:', err);
          navigate('/vscode/dashboard');
        }
      } else {
        setError("Temporary access token not available for dashboard access.");
      }
    }
  };
  
  // Post-signin auto-checkout. Provider-aware: routes the buyer to whichever
  // provider the backend reports as active (Whop by default), instead of always
  // sending them to Stripe.
  const createStripeCheckoutDirect = async (planName, isYearly, token) => {
    const billing = isYearly ? 'yearly' : 'monthly';

    // Open the returned hosted-checkout URL the same way for both providers:
    // new tab if possible, same-window fallback if the popup is blocked.
    const redirectToCheckout = (checkoutUrl) => {
      if (!checkoutUrl) {
        navigate('/pricing?error=checkout_failed');
        return;
      }
      const checkoutWindow = window.open(checkoutUrl, '_blank');
      if (checkoutWindow) {
        navigate('/pricing');
      } else {
        window.location.href = checkoutUrl;
      }
    };

    try {
      const apikey = localStorage.getItem('api_key')
      const userInfo = await getUserInfoFromAuth()
      const email = userInfo?.email

      // Resolve the active provider from the backend before building the request.
      const providersInfo = await fetchPaymentProviders();
      const activeProvider = getActiveProvider(providersInfo);

      // Whop path — hosted checkout. Lifetime is a one-off; tiers are recurring.
      if (activeProvider === WHOP) {
        let result;
        if (planName === 'Lifetime') {
          const successUrl = `${window.location.origin}/vscode/dashboard?lifetime=success`;
          result = await createWhopLifetimeCheckout({ email, user_id: apikey, success_url: successUrl });
        } else {
          const planId = resolvePlanId(WHOP, planName, billing, providersInfo);
          if (!planId) {
            console.error('Whop plan id not found for plan:', planName, billing);
            navigate('/pricing');
            return;
          }
          result = await createWhopCheckout({ plan_id: planId, email, user_id: apikey });
        }
        redirectToCheckout(result?.url || result?.checkout_url);
        return;
      }

      // Stripe path (default fallback).
      const priceId = getPriceId(planName, billing);
      if (!priceId) {
        console.error('Price ID not found for plan:', planName, billing);
        navigate('/pricing');
        return;
      }

      // Lifetime is a one-off price: it goes through upgrade_plan (payment-mode
      // checkout, current plan credited), same as the pricing page. The
      // subscription checkout endpoint below can't take a one-time price.
      if (planName === 'Lifetime') {
        const res = await fetch(`${serverBaseUrl}/payment/upgrade_plan`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_price_id: priceId,
            success_url: `${window.location.origin}/vscode/dashboard?lifetime=success`,
            cancel_url: `${window.location.origin}/pricing`,
          }),
        });
        const result = res.ok ? await res.json() : null;
        if (result?.success) {
          navigate('/vscode/dashboard?lifetime=success');
          return;
        }
        redirectToCheckout(result?.checkout_url);
        return;
      }

      const response = await fetch(`${serverBaseUrl}/payment/create_checkout_url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          price_id: priceId,
          payment_type: "stripe",
          // Explicit so the backend doesn't fall back to the Whop default and
          // hijack this Stripe checkout (mirrors the pricing-page Stripe branch).
          provider: "stripe",
          user_id: apikey,
          email: email
        })
      });

      if (response.ok) {
        const responseData = await response.json();
        redirectToCheckout(responseData.url || responseData.checkout_url);
      } else {
        navigate('/pricing?error=payment_failed');
      }
    } catch (error) {
      navigate('/pricing?error=system_error');
    }
  };

  const handleAuth = useCallback(async (provider, code, isSignUp) => {
    setLoading(true);
    setError(null);
    try {
      // Check for team invite
      const teamId = localStorage.getItem('pending_team_join_id');
      const hasTeamId = isSignUp && teamId;

      let url;
      let payload = { auth_provider: provider, auth_code: code };

      if (hasTeamId) {
        // Use new /auth/register endpoint for team OAuth signups
        url = API_ENDPOINTS.REGISTER;
        // Generate unique ID for new user
        const userId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
        payload = {
          ...payload,
          team_id: teamId,
          id: userId,
          user_type: 'developer'
        };
      } else {
        url = isSignUp ? API_ENDPOINTS.SIGNUP : API_ENDPOINTS.SIGNIN;
      }

      const response = await api.post(url, payload);

      // Handle different response format for team registration
      if (hasTeamId && response.data?.data?.token) {
        // New endpoint returns { message, data: { token } }
        const token = response.data.data.token;
        processSuccessfulAuth({
          access_token: { access_token: token },
          temp_access_token: { access_token: token },
          email: response.data.data.email || null,
          apikey: response.data.data.id || null
        }, isSignUp);

        // Clean up team join data after successful registration
        localStorage.removeItem('pending_team_join_id');
      } else {
        processSuccessfulAuth(response.data, isSignUp);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleEmailAuth = async (payload, isSignUp) => {
    setLoading(true);
    setError(null);
    try {
      // Use the team-aware registration endpoint if team_id is present
      const hasTeamId = payload.team_id;
      let url;

      if (isSignUp && hasTeamId) {
        // Use /auth/register endpoint for team signups
        url = API_ENDPOINTS.REGISTER;
      } else {
        url = isSignUp ? API_ENDPOINTS.SIGNUP : API_ENDPOINTS.SIGNIN;
      }

      // Debug: Log the request being made
      console.log('=== Auth Request Debug ===');
      console.log('Endpoint:', url);
      console.log('Payload:', JSON.stringify(payload, null, 2));
      console.log('Has team_id:', hasTeamId);
      console.log('==========================');

      const response = await api.post(url, payload);

      // Debug: Log the response
      console.log('=== Auth Response Debug ===');
      console.log('Response data:', JSON.stringify(response.data, null, 2));
      console.log('===========================');

      // Email verification flow (new email signups only — OAuth responses
      // never carry this flag): do NOT complete auth yet. Park the email and
      // send the user to the OTP screen; tokens are issued by /verify-email.
      if (response.data?.verification_required === true) {
        localStorage.setItem('pending_verification_email', payload.email || '');
        navigate(`/vscode/verify-email${window.location.search}`);
        return;
      }

      // Handle different response format for team registration
      if (isSignUp && hasTeamId && response.data?.data?.token) {
        // New endpoint returns { message, data: { token } }
        // Convert to expected format for processSuccessfulAuth
        const token = response.data.data.token;
        processSuccessfulAuth({
          access_token: { access_token: token },
          temp_access_token: { access_token: token },
          email: payload.email,
          apikey: payload.id || null
        }, isSignUp);

        // Clean up team join data after successful registration
        localStorage.removeItem('pending_team_join_id');
      } else {
        processSuccessfulAuth(response.data, isSignUp);
      }
    } catch (err) {
      // Unverified account signing in: backend sent a fresh code already —
      // route to the OTP screen instead of showing an error.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.error === 'email_verification_required') {
        localStorage.setItem('pending_verification_email', detail.email || payload.email || '');
        navigate(`/vscode/verify-email${window.location.search}`);
        return;
      }
      setError((typeof detail === 'string' && detail) || err.response?.data?.message || err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Confirm the emailed OTP. Backend returns tokens in the same shape as
  // signup/signin, so the normal auth completion (incl. the vscode://
  // handoff via onboarding) runs from here.
  const handleVerifyEmail = async (email, otp) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(API_ENDPOINTS.VERIFY_EMAIL, { email, otp });
      localStorage.removeItem('pending_verification_email');
      await processSuccessfulAuth(response.data, true);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((typeof detail === 'string' && detail) || err.message || "Verification failed");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (email) => {
    try {
      await api.post(API_ENDPOINTS.RESEND_VERIFICATION, { email });
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((typeof detail === 'string' && detail) || "Could not resend the code.");
      return false;
    }
  };

  const handleResetPassword = async (email, code, password) => {
    setLoading(true);
    setError(null);
    try {
      await api.post(API_ENDPOINTS.RESET_PASSWORD, { email, otp: code, new_password: password });
      alert("Password has been reset successfully!");
      navigate('/vscode/signin');
    } catch (err) {
      setError(err.response?.data?.message || err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };


  // Logout function - clears all auth data
  const logout = useCallback(() => {
    // Clear all auth-related localStorage items
    localStorage.removeItem('is_authenticated');
    localStorage.removeItem('auth_data');
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('web_access_token');
    localStorage.removeItem('vscode_jwt_token');
    localStorage.removeItem('api_key');
    localStorage.removeItem('user_email');
    localStorage.removeItem('email');
    localStorage.removeItem('vscode_referrer');
    localStorage.removeItem('vscode_callback_url');
    localStorage.removeItem('pending_team_join_id');

    // Clear user state
    setUser(null);
    setErrorState(null);

    // Redirect to signin page
    navigate('/vscode/signin');
  }, [navigate]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const authFlag = localStorage.getItem('is_authenticated');
    const authData = localStorage.getItem('auth_data');
    const token = localStorage.getItem('web_access_token') || localStorage.getItem('jwt_token');

    if (authFlag === 'true' && authData && token) {
      try {
        const parsedAuthData = JSON.parse(authData);
        // Check if auth data is not too old (7 days)
        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        if (parsedAuthData.timestamp && parsedAuthData.timestamp > sevenDaysAgo) {
          return true;
        }
      } catch (e) {
        return false;
      }
    }
    return false;
  }, []);

  const authContext = {
    user,
    loading,
    error,
    setUser,
    setLoading,
    setError,
    handleAuth,
    handleEmailAuth,
    handleVerifyEmail,
    handleResendVerification,
    handleResetPassword,
    processSuccessfulAuth,
    logout,
    isAuthenticated,
  };

  return (
    <VsCodeAuthContext.Provider value={authContext}>
      {children}
    </VsCodeAuthContext.Provider>
  );
};
